import { useEffect, useRef, useState } from 'react';
import { Checkbox, Tooltip } from 'antd';
import {
  DelayEventWhere,
  DelayNoteAddedBy,
  DelayStatusMilestone,
  type DelayWaitEvent,
} from '@/proto/logiblocks/logiblocks';
import type { PanelScreen } from '@/pages/Logiblocks/types';
import CustomRadioGroup from '@/components/CustomRadioGroup';
import { FieldErrorLabel } from '../shared';
import type { ValidationError } from '@/pages/Logiblocks/fieldValidation';
import { DelayAlertTypeChecklist } from './DelayAlertTypeChecklist';
import { DelayReplyToSelect } from './DelayReplyToSelect';
import { DelayWhereSelect } from './DelayWhereSelect';
import {
  ACTION_CHOICE_OPTIONS,
  actionChoiceOf,
  actionSettingsOfChoice,
  CREATED_STATUS_OPTION,
  EVENT_KIND_META,
  eventErrorMessage,
  getEventKind,
  NOTE_ADDED_BY_OPTIONS,
  STATUS_OPTIONS,
  type ActionChoice,
} from './delayUntilEventModel';
import { useScrollToFirstError } from './useScrollToFirstError';
import styles from './DelayUntilEvent.module.css';

interface DelayEventDetailProps {
  /** The Delay node being edited (the Reply to list is the nodes before it). */
  nodeId: string;
  /** The event to edit. Changes stay in this screen until it is confirmed. */
  event: DelayWaitEvent;
  /** True when the event is not on the Delay yet: the screen was reached from "Add event". */
  isNew: boolean;
  isReadOnly: boolean;
  onConfirm: (event: DelayWaitEvent) => void;
  /** Back arrow and Cancel: leave the Delay as it was. */
  onCancel: () => void;
  setPanelScreen?: (screen: PanelScreen | null) => void;
}

/**
 * One event's settings screen. Edits a draft and hands it back through the
 * panel's main button ("Add event" or "Save"); back and Cancel leave the Delay
 * as it was. The panel draws the title, back arrow and footer.
 */
export const DelayEventDetail = ({
  nodeId,
  event,
  isNew,
  isReadOnly,
  onConfirm,
  onCancel,
  setPanelScreen,
}: DelayEventDetailProps) => {
  const [draft, setDraft] = useState<DelayWaitEvent>(event);
  const [attempted, setAttempted] = useState(false);

  const kind = getEventKind(draft);
  const meta = kind ? EVENT_KIND_META[kind] : undefined;
  // The error sits under a long list: bring it into view when Add event or Save is refused.
  const detailRef = useScrollToFirstError<HTMLDivElement>(
    attempted && !!eventErrorMessage(draft)
  );

  const handleConfirm = () => {
    if (eventErrorMessage(draft)) {
      setAttempted(true);
      return;
    }
    onConfirm(draft);
  };

  // The panel's buttons call the latest of these, not the ones from the render
  // that opened the screen.
  const confirmRef = useRef(handleConfirm);
  confirmRef.current = handleConfirm;
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;

  const title = meta?.label ?? 'Event';
  useEffect(() => {
    setPanelScreen?.({
      title,
      backLabel: isNew ? 'Back to menu' : 'Delay',
      onBack: () => cancelRef.current(),
      ...(isReadOnly
        ? {}
        : {
            primaryLabel: isNew ? 'Add event' : 'Save',
            onPrimary: () => confirmRef.current(),
          }),
    });
    return () => setPanelScreen?.(null);
  }, [setPanelScreen, title, isNew, isReadOnly]);

  if (!kind || !meta) {
    return (
      <div className={styles.screen}>
        <div className={styles.emptyText}>
          This event type cannot be edited here.
        </div>
      </div>
    );
  }

  const errorMessage = attempted ? eventErrorMessage(draft) : undefined;
  const error: ValidationError | undefined = errorMessage
    ? { type: 'REQUIRED_FIELD', field: meta.label, message: errorMessage }
    : undefined;
  const actionTaken = draft.alert_action_taken;

  return (
    <div ref={detailRef} className={styles.detailScreen}>
      <p className={styles.detailSubtitle}>
        Continue when: {meta.continueWhen}
      </p>

      {draft.alert_triggered && (
        <DelayAlertTypeChecklist
          value={draft.alert_triggered.alerts}
          onChange={(alerts) =>
            setDraft({ id: draft.id, alert_triggered: { alerts } })
          }
          disabled={isReadOnly}
          error={error}
        />
      )}

      {actionTaken && (
        <DelayAlertTypeChecklist
          value={actionTaken.alerts}
          onChange={(alerts) =>
            setDraft({
              id: draft.id,
              alert_action_taken: { ...actionTaken, alerts },
            })
          }
          disabled={isReadOnly}
          error={error}
          header={
            <ActionRadio
              event={actionTaken}
              disabled={isReadOnly}
              onChange={(choice) =>
                setDraft({
                  id: draft.id,
                  alert_action_taken: {
                    ...actionTaken,
                    ...actionSettingsOfChoice(choice),
                  },
                })
              }
            />
          }
        />
      )}

      {draft.status_progress_update && (
        <StatusChecklist
          value={draft.status_progress_update.statuses}
          disabled={isReadOnly}
          error={error}
          onChange={(statuses) =>
            setDraft({ id: draft.id, status_progress_update: { statuses } })
          }
        />
      )}

      {draft.reply_received && (
        <div>
          <label className={styles.fieldLabel}>Reply to</label>
          <DelayReplyToSelect
            nodeId={nodeId}
            value={draft.reply_received.source_node_ids}
            disabled={isReadOnly}
            onChange={(sourceNodeIds) =>
              setDraft({
                id: draft.id,
                reply_received: { source_node_ids: sourceNodeIds },
              })
            }
          />
        </div>
      )}
      {draft.document_added && (
        <div>
          <DelayWhereSelect
            value={draft.document_added.where}
            disabled={isReadOnly}
            onChange={(where: DelayEventWhere) =>
              setDraft({ id: draft.id, document_added: { where } })
            }
          />
          <FieldErrorLabel error={error} />
        </div>
      )}
      {draft.note_added && (
        <>
          <div>
            <DelayWhereSelect
              value={draft.note_added.where}
              disabled={isReadOnly}
              onChange={(where: DelayEventWhere) =>
                setDraft({
                  id: draft.id,
                  note_added: { ...draft.note_added!, where },
                })
              }
            />
            <FieldErrorLabel error={error} />
          </div>
          <div className={styles.detailRadioGroup}>
            <label className={styles.detailRadioLabel}>Added by</label>
            <div className={styles.detailRadioList}>
              <CustomRadioGroup
                options={NOTE_ADDED_BY_OPTIONS}
                value={draft.note_added.added_by}
                disabled={isReadOnly}
                onChange={(e) =>
                  setDraft({
                    id: draft.id,
                    note_added: {
                      ...draft.note_added!,
                      added_by: e.target.value as DelayNoteAddedBy,
                    },
                  })
                }
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const ActionRadio = ({
  event,
  disabled,
  onChange,
}: {
  event: NonNullable<DelayWaitEvent['alert_action_taken']>;
  disabled: boolean;
  onChange: (choice: ActionChoice) => void;
}) => (
  <div className={styles.detailRadioGroup}>
    <label className={styles.detailRadioLabel}>Action</label>
    {/* Saved lists that match none of the five choices leave the radio blank. */}
    <div className={styles.detailRadioList}>
      <CustomRadioGroup
        options={ACTION_CHOICE_OPTIONS}
        value={actionChoiceOf(event)}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as ActionChoice)}
      />
    </div>
  </div>
);

const StatusChecklist = ({
  value,
  disabled,
  error,
  onChange,
}: {
  value: DelayStatusMilestone[];
  disabled: boolean;
  error?: ValidationError;
  onChange: (statuses: DelayStatusMilestone[]) => void;
}) => {
  const offered = STATUS_OPTIONS.map((o) => o.value);
  // A saved status that is not offered has no checkbox to untick, so any change drops it.
  const toggle = (status: DelayStatusMilestone, checked: boolean) =>
    onChange(
      offered.filter((s) => (s === status ? checked : value.includes(s)))
    );
  return (
    <div>
      <label className={styles.fieldLabel}>Shipment status change</label>
      <div className={styles.statusList}>
        {/* Created already happened before the wait starts, so it can't be chosen. */}
        <Tooltip title='Already happened before wait started'>
          <span className={styles.createdOption}>
            <Checkbox
              checked={value.includes(CREATED_STATUS_OPTION.value)}
              disabled
            >
              {CREATED_STATUS_OPTION.label}
            </Checkbox>
          </span>
        </Tooltip>
        {STATUS_OPTIONS.map((option) => (
          <Checkbox
            key={option.value}
            checked={value.includes(option.value)}
            disabled={disabled}
            onChange={(e) => toggle(option.value, e.target.checked)}
          >
            {option.label}
          </Checkbox>
        ))}
        <FieldErrorLabel error={error} />
      </div>
    </div>
  );
};
